import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import PhotoUploadModal from './PhotoUploadModal';
import LogoMark from './LogoMark';

function CompletedProjectModal({
  savedPattern,
  pattern,
  photos,
  colors,
  fonts,
  user,
  supabase,
  onClose,
  onPhotosUpdated
}) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showAddPhotos, setShowAddPhotos] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState(savedPattern?.notes || '');

  if (!savedPattern || !pattern) return null;

  const handleDeletePhoto = async (photoId) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    // Delete from storage
    const filePath = photo.photo_url.split('/project-photos/')[1];
    const { error: storageError } = await supabase.storage
      .from('project-photos')
      .remove([filePath]);

    if (storageError) {
      console.error('Error deleting from storage:', storageError);
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('project_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('Error deleting from database:', dbError);
      alert('Failed to delete photo. Please try again.');
    } else {
      alert('Photo deleted!');
      setShowDeleteConfirm(null);
      onPhotosUpdated();

      // If we deleted the current photo, go back to first photo
      if (selectedPhotoIndex >= photos.length - 1) {
        setSelectedPhotoIndex(Math.max(0, photos.length - 2));
      }
    }
  };

  const handleSaveNotes = async () => {
    const { error } = await supabase
      .from('saved_patterns')
      .update({ notes: notesText || null })
      .eq('id', savedPattern.id);

    if (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    } else {
      alert('✅ Notes saved!');
      setEditingNotes(false);
      onPhotosUpdated(); // Refresh the data
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '2rem',
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '20px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          fontFamily: fonts.body
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: '1.5rem',
            left: 'calc(100% - 60px)',
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1.5rem',
            color: colors.brownGray,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10,
            marginBottom: '-56px',
            marginRight: '1rem'
          }}
        >
          ×
        </button>

        {/* Photo Gallery Section */}
        {photos && photos.length > 0 ? (
          <div style={{
            position: 'relative',
            background: colors.charcoal,
            minHeight: '400px'
          }}>
            {/* Main Photo */}
            <img
              src={photos[selectedPhotoIndex].photo_url}
              alt={`Project photo ${selectedPhotoIndex + 1}`}
              style={{
                width: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
                display: 'block'
              }}
            />

            {/* Photo Navigation */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedPhotoIndex(Math.max(0, selectedPhotoIndex - 1))}
                  disabled={selectedPhotoIndex === 0}
                  style={{
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: selectedPhotoIndex === 0 ? 'not-allowed' : 'pointer',
                    opacity: selectedPhotoIndex === 0 ? 0.3 : 1,
                    fontSize: '1.5rem',
                    color: colors.charcoal
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={() => setSelectedPhotoIndex(Math.min(photos.length - 1, selectedPhotoIndex + 1))}
                  disabled={selectedPhotoIndex === photos.length - 1}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: selectedPhotoIndex === photos.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: selectedPhotoIndex === photos.length - 1 ? 0.3 : 1,
                    fontSize: '1.5rem',
                    color: colors.charcoal
                  }}
                >
                  ›
                </button>
              </>
            )}

            {/* Photo Counter */}
            <div style={{
              position: 'absolute',
              bottom: '1rem',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              {selectedPhotoIndex + 1} / {photos.length}
            </div>

            {/* Caption */}
            {photos[selectedPhotoIndex].caption && (
              <div style={{
                position: 'absolute',
                bottom: '3.5rem',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                borderRadius: '12px',
                fontSize: '0.95rem',
                maxWidth: '80%',
                textAlign: 'center'
              }}>
                {photos[selectedPhotoIndex].caption}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: `linear-gradient(135deg, ${colors.sageGreen} 0%, #8e9775 100%)`,
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '4rem'
          }}>
            <LogoMark width={160} height={64} color="#fff" />
          </div>
        )}

        {/* Photo Thumbnails */}
        {photos && photos.length > 1 && (
          <div style={{
            padding: '1rem',
            background: colors.bgCream,
            display: 'flex',
            gap: '0.5rem',
            overflowX: 'auto'
          }}>
            {photos.map((photo, idx) => (
              <div
                key={photo.id}
                onClick={() => setSelectedPhotoIndex(idx)}
                style={{
                  width: '80px',
                  height: '80px',
                  flexShrink: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: idx === selectedPhotoIndex ? `3px solid ${colors.mustard}` : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <img
                  src={photo.photo_url}
                  alt={`Thumbnail ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* Completed Badge */}
          <div style={{
            display: 'inline-block',
            background: colors.mustard,
            color: '#fff',
            padding: '0.5rem 1rem',
            borderRadius: '50px',
            fontSize: '0.85rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            ✅ Completed
          </div>

          {/* Pattern Title */}
          <h2
            style={{
              fontSize: '2rem',
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: '0.5rem'
            }}
          >
            {pattern.title}
          </h2>

          {/* Dates */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: colors.darkGray
          }}>
            {savedPattern.started_at && (
              <div>
                <strong>Started:</strong> {new Date(savedPattern.started_at).toLocaleDateString()}
              </div>
            )}
            {savedPattern.completed_at && (
              <div>
                <strong>Completed:</strong> {new Date(savedPattern.completed_at).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{
            background: colors.bgCream,
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: `2px solid ${colors.borderGreige}`
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: colors.charcoal,
                fontWeight: '700',
                margin: 0
              }}>
                Project Notes
              </h3>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  style={{
                    background: 'none',
                    border: `2px solid ${colors.sageGreen}`,
                    padding: '0.5rem 1rem',
                    borderRadius: '50px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: colors.sageGreen,
                    cursor: 'pointer'
                  }}
                >
                  ✏️ Edit
                </button>
              )}
            </div>

            {editingNotes ? (
              <>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Add notes about your project..."
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '1rem',
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontFamily: fonts.body,
                    resize: 'vertical',
                    marginBottom: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setNotesText(savedPattern.notes || '');
                      setEditingNotes(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.borderGreige,
                      color: colors.charcoal,
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: colors.sageGreen,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Save Notes
                  </button>
                </div>
              </>
            ) : (
              <p style={{
                color: notesText ? colors.darkGray : colors.brownGray,
                fontSize: '0.95rem',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                fontStyle: notesText ? 'normal' : 'italic'
              }}>
                {notesText || 'No notes yet. Click Edit to add notes about your project!'}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: photos && photos.length > 0 ? '1fr 1fr 1fr' : '1fr 1fr',
            gap: '1rem',
            paddingTop: '1rem',
            borderTop: `2px solid ${colors.borderGreige}`
          }}>
            {/* View Original Pattern */}
            <a
              href={pattern.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '1rem',
                background: '#fff',
                border: `2px solid ${colors.dustyRust}`,
                borderRadius: '12px',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: colors.dustyRust,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                textDecoration: 'none'
              }}
            >
              View Pattern
              <ExternalLink size={16} />
            </a>

            {/* Add More Photos */}
            <button
              onClick={() => setShowAddPhotos(true)}
              style={{
                padding: '1rem',
                background: colors.sageGreen,
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              📸 Add Photos
            </button>

            {/* Delete Current Photo */}
            {photos && photos.length > 0 && (
              <button
                onClick={() => setShowDeleteConfirm(photos[selectedPhotoIndex].id)}
                style={{
                  padding: '1rem',
                  background: '#fff',
                  border: `2px solid ${colors.dustyRust}`,
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: colors.dustyRust,
                  cursor: 'pointer'
                }}
              >
                🗑️ Delete Photo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%'
            }}
          >
            <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: colors.charcoal }}>
              Delete this photo?
            </h3>
            <p style={{ color: colors.darkGray, marginBottom: '1.5rem' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.borderGreige,
                  color: colors.charcoal,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePhoto(showDeleteConfirm)}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.dustyRust,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Photos Modal */}
      {showAddPhotos && (
        <PhotoUploadModal
          savedPattern={savedPattern}
          pattern={pattern}
          colors={colors}
          fonts={fonts}
          user={user}
          supabase={supabase}
          onClose={() => setShowAddPhotos(false)}
          onPhotosUploaded={(photos) => {
            setShowAddPhotos(false);
            onPhotosUpdated();
          }}
        />
      )}
    </div>
  );
}

export default CompletedProjectModal;
